// Silent PWA refresh: the generated /sw.js (registerType 'autoUpdate') takes
// control of an open tab after a deploy, but the page keeps running the old
// build until someone loads it again. This watcher asks the app-root service
// worker for updates and, once a new one has taken control, reloads the page
// at a moment where nothing can be lost (tab idle, nothing typed, nothing
// open or playing). Every branch fails closed: if anything is unsupported or
// throws, it does nothing. The firebase-messaging service worker is ignored.

const APP_SW_SUFFIX = '/sw.js';
const CHECK_INTERVAL_MS = 60 * 60 * 1000;
const CHECK_THROTTLE_MS = 5 * 60 * 1000;
const MIN_HIDDEN_MS = 10 * 60 * 1000;
const RELOAD_GUARD_MS = 30 * 60 * 1000;
const RELOAD_GUARD_KEY = 'vivideo_app_update_reloaded_at';

// Input types whose value is text a user could have typed.
const TEXT_INPUT_TYPES = new Set([
  '', 'text', 'search', 'url', 'tel', 'email', 'password', 'number', 'date', 'datetime-local', 'time', 'month', 'week',
]);

let started = false;
let updateReady = false;
let hadControllerAtLoad = false;
let hiddenSince: number | null = null;
let lastCheckAt = 0;
// Mirrors the sessionStorage guard so a blocked/throwing storage still caps
// reloads at one per page life.
let lastReloadAt = 0;

/** True for the app's own service worker script, false for firebase-messaging-sw.js. */
function isAppServiceWorker(scriptURL: string | undefined | null): boolean {
  if (!scriptURL) return false;
  const path = scriptURL.split('?')[0].split('#')[0];
  return path.endsWith(APP_SW_SUFFIX);
}

/** The registration scoped at the app root whose script is /sw.js, or null. */
async function getAppRegistration(): Promise<ServiceWorkerRegistration | null> {
  const registration = await navigator.serviceWorker.getRegistration('/');
  if (!registration) return null;
  const script =
    registration.active?.scriptURL ||
    registration.waiting?.scriptURL ||
    registration.installing?.scriptURL ||
    '';
  return isAppServiceWorker(script) ? registration : null;
}

async function checkForUpdate(): Promise<void> {
  try {
    const now = Date.now();
    if (lastCheckAt && now - lastCheckAt < CHECK_THROTTLE_MS) return;
    lastCheckAt = now;
    const registration = await getAppRegistration();
    if (!registration) return;
    await registration.update();
  } catch {
    // Offline, blocked, or unsupported — try again on the next opportunity.
  }
}

function hasTypedText(): boolean {
  const fields = document.querySelectorAll('textarea, input');
  for (const field of Array.from(fields)) {
    if (field instanceof HTMLTextAreaElement) {
      if (field.value.trim() !== '') return true;
      continue;
    }
    const input = field as HTMLInputElement;
    const type = (input.getAttribute('type') || '').toLowerCase();
    if (TEXT_INPUT_TYPES.has(type) && (input.value || '').trim() !== '') return true;
  }
  const editables = document.querySelectorAll('[contenteditable]:not([contenteditable="false"])');
  for (const editable of Array.from(editables)) {
    if ((editable.textContent || '').trim() !== '') return true;
  }
  return false;
}

function hasPendingUpload(): boolean {
  const pickers = document.querySelectorAll('input[type="file"]');
  for (const picker of Array.from(pickers)) {
    const files = (picker as HTMLInputElement).files;
    if (files && files.length > 0) return true;
  }
  return false;
}

/**
 * Modals here are portals into <body> rendering a full-screen `fixed inset-0`
 * overlay (VideoPlayerModal, UpgradeModal, the asset pickers), plus a few
 * in-tree ones and the scroll lock that Modal.tsx sets. Any of those counts.
 */
function hasOpenOverlay(): boolean {
  if (document.querySelector('[role="dialog"], [aria-modal="true"], dialog[open], .fixed.inset-0')) return true;
  if (document.body?.style?.overflow === 'hidden') return true;
  return false;
}

function hasPlayingVideo(): boolean {
  const videos = document.querySelectorAll('video');
  for (const video of Array.from(videos)) {
    const media = video as HTMLVideoElement;
    if (!media.paused && !media.ended) return true;
  }
  return false;
}

/** Anything that says the user is mid-task; a reload would interrupt it. */
function isPageBusy(): boolean {
  if (document.querySelector('[aria-busy="true"]')) return true;
  if (hasOpenOverlay()) return true;
  if (hasTypedText()) return true;
  if (hasPendingUpload()) return true;
  if (hasPlayingVideo()) return true;
  return false;
}

/** At most one automatic reload per 30 minutes per tab, storage or not. */
function takeReloadSlot(): boolean {
  const now = Date.now();
  if (lastReloadAt && now - lastReloadAt < RELOAD_GUARD_MS) return false;
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY);
    const stored = raw ? Number(raw) : 0;
    if (stored && Number.isFinite(stored) && now - stored < RELOAD_GUARD_MS) return false;
  } catch {
    // Storage blocked — the in-memory guard above still holds.
  }
  lastReloadAt = now;
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    // Ignored, as above.
  }
  return true;
}

function maybeReload(hiddenForMs: number): void {
  if (!updateReady) return;
  if (hiddenForMs < MIN_HIDDEN_MS) return;
  if (navigator.onLine === false) return;
  if (isPageBusy()) return;
  if (!takeReloadSlot()) return;
  console.debug('[AppUpdate] new build active — reloading idle tab');
  window.location.reload();
}

function onControllerChange(): void {
  try {
    // No controller when the page loaded means this is the first-ever install,
    // not a new release.
    if (!hadControllerAtLoad) return;
    if (!isAppServiceWorker(navigator.serviceWorker.controller?.scriptURL)) return;
    updateReady = true;
  } catch {
    // Ignored.
  }
}

function onVisibilityChange(): void {
  try {
    if (document.visibilityState === 'hidden') {
      hiddenSince = Date.now();
      return;
    }
    if (document.visibilityState !== 'visible') return;
    const hiddenForMs = hiddenSince === null ? 0 : Date.now() - hiddenSince;
    hiddenSince = null;
    void checkForUpdate();
    maybeReload(hiddenForMs);
  } catch {
    // Ignored.
  }
}

/**
 * Start watching for a newer build. Safe to call more than once; does nothing
 * on the dev server or where service workers are unsupported, and never throws.
 */
export function startAppUpdateWatch(): void {
  try {
    // import.meta.env.DEV is also true under Vitest, where the tests drive this.
    if (import.meta.env.DEV && import.meta.env.MODE !== 'test') return;
    if (started) return;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !navigator.serviceWorker) return;
    started = true;
    hadControllerAtLoad = !!navigator.serviceWorker.controller;
    if (document.visibilityState === 'hidden') hiddenSince = Date.now();
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.setInterval(() => { void checkForUpdate(); }, CHECK_INTERVAL_MS);
  } catch {
    // Ignored — the app must start even if none of this is available.
  }
}
